import {
  AccessTypeEnum,
  ApiTypesEnum,
  CarrierOrGroupEnum,
  EntityTypeEnum,
  FindCarrierTypeEnum,
  GlobalDayWithInterval,
  GlobalTimeslots,
  MergeInfoInDTO,
  OpeningModeEnum,
  SaveDistributionRequestInDTO,
  SaveExpeditionLocationRequestInDTO,
  SaveExpeditionRequestInDTO,
  SelectedItemTypeEnum,
} from '@api/ringil-api.v2.tsx';
import { ExpeditionLocality, LocalityEntryModeEnum, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import {
  CarrierDistributionSelectorData,
  getSelectedUserIds,
  resolveResponseDeadline,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import {
  apiPragueFromDayjsLocalWithFormat,
  apiUtcFromDayjs,
  dayjsFromDateAtMidnightInTz,
  dayjsFromDayAndTimeStrings,
} from '@utils/dateWithTz.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { isEmptyArray } from 'formik';
import { PrefillOptions } from '@components/RingilComponents/expeditionPrefillOptions.ts';
import { timeslotLocalDay } from '@components/RingilComponents/FedexPickupForm.tsx';
import { toContractedCarrierInDTO, toPriceTableDTO } from '@components/ringil3/Features/Contracted/types.ts';

/**
 * Form model → API request mapping for the create-shipment form.
 *
 * Everything here is pure: the caller supplies whatever it would otherwise read from context
 * (the user's default timezone, the entity the form was pre-filled from). That keeps the mapping
 * unit-testable and lets other prefill sources reuse it.
 */

/** Backfills `utcDay` on timeslot days, which the API needs but the timeslot editor does not produce. */
const mapTimeslots = (timeslots: GlobalTimeslots, timezoneId?: string): GlobalTimeslots => {
  if (!timeslots || !timeslots.dayWithInterval) return timeslots;

  const tz = timeslots.timezoneId || timezoneId || 'Europe/Prague';

  return {
    timezoneId: tz,
    dayWithInterval: timeslots.dayWithInterval.map((day: GlobalDayWithInterval) => ({
      ...day,
      utcDay: day.utcDay || (day.day ? (dayjsFromDateAtMidnightInTz(new Date(day.day), tz) as any)?.format('YYYY-MM-DD') : undefined),
    })),
  };
};

const emailKey = (email?: string) => (email || '').trim().toLowerCase();

const uniqueByEmail = <T extends { contactEmail?: string }>(contacts: T[]): T[] =>
  contacts.filter(
    (contact, index) => !emailKey(contact.contactEmail) || contacts.findIndex(other => emailKey(other.contactEmail) === emailKey(contact.contactEmail)) === index,
  );

export const mapLocation = (l: ExpeditionLocality): SaveExpeditionLocationRequestInDTO => {
  if (l.localityMode == LocalityEntryModeEnum.FROM_LOCATIONS) {
    return {
      companyLocationId: l.location.locationId,
      additionalContacts: uniqueByEmail([
        ...(l.additionalContacts || []),
        ...(l.additionalPersons || []).map(person => ({ contactEmail: person.email, contactName: person.name })),
      ]),
      address: l.address,
      loadingReference: l.loadingReference,
      selectedPerson: l.selectedPerson,
      orders: l.orders,
      timeslots: mapTimeslots(l.timeslots, l.location.timezoneId),
      unloadingOrderIds: l.unloadingOrderIds,
      notifySelectedPerson: false,
      timezoneId: l.location.timezoneId,
      locationNote: l.location.locationNote,
      // Only a stop prefilled from a Linka carries its own requirements.
      ...(l.requirements?.length ? { requirements: l.requirements } : {}),
    };
  }

  return {
    companyLocationId: null,
    additionalContacts: uniqueByEmail(
      (l.manualAddress.additionalPersons || []).map(t => ({
        contactEmail: t.email,
        contactName: t.name,
      })),
    ),
    address: {
      name: l.manualAddress.name,
      city: l.manualAddress.city,
      country: l.manualAddress.countryCode3,
      postalCode: l.manualAddress.postalCode,
      streetNr: l.manualAddress.streetNr,
    },
    loadingReference: l.loadingReference,
    selectedPerson:
      [l.manualAddress.mainContactPerson, l.manualAddress.mainContactPhone, l.manualAddress.mainContactEmail].filter(t => t != null).length > 0
        ? {
          contactEmail: l.manualAddress.mainContactEmail,
          contactName: l.manualAddress.mainContactPerson,
          contactPhone: l.manualAddress.mainContactPhone,
        }
        : null,
    orders: l.orders,
    timeslots: mapTimeslots(l.timeslots, l.manualAddress.timezoneId),
    unloadingOrderIds: l.unloadingOrderIds,
    notifySelectedPerson: l.manualAddress.notifySelectedPerson,
    timezoneId: l.manualAddress.timezoneId,
    locationNote: undefined,
  };
};

const mapSelectedItem = (carrier: CarrierDistributionSelectorData['selectedItems'][number]) => {
  let selectedItemType: SelectedItemTypeEnum;
  switch (carrier.carrierType) {
    case 'GROUP':
      selectedItemType = SelectedItemTypeEnum.GROUP;
      break;
    case FindCarrierTypeEnum.API:
      selectedItemType = SelectedItemTypeEnum.API;
      break;
    default:
      selectedItemType = SelectedItemTypeEnum.CARRIER;
  }

  return {
    id: carrier.id,
    carrierOrGroup:
      carrier.carrierType === 'CARRIER' || carrier.carrierType === 'CARRIER_USER' ? CarrierOrGroupEnum.CARRIER : CarrierOrGroupEnum.GROUP,
    selectedItemType: selectedItemType,
    userId: Array.isArray(carrier.userIds) && carrier.userIds.length > 0 ? carrier.userIds[0] : null,
  };
};

/**
 * Selector form data → the V3 distribution request envelope (`fixedRequest` / `spotRequest` /
 * `directAssign`). Shared by the create-shipment form and the bulk "Poptat dopravce" sidebar; the
 * pickup timezone and timeslots are only needed to place a DHL/FedEx courier pickup time, so callers
 * without them (bulk, where API carriers are excluded) leave them out.
 */
/**
 * CONTRACTED ("Smluvní dopravce"): the carriers with their contracted prices (as edited for this
 * shipment), plus the laden factor and deadline the same way spot and fixed send them. No price —
 * the backend computes it from the contract.
 */
export const mapContractedDistributionToRequest = (distribution: CarrierDistributionSelectorData, defaultTimezoneId: string): SaveDistributionRequestInDTO => {
  if (!distribution?.contractedCarriers?.length || !distribution.contractedPriceTable) return null;
  const resolvedDeadline = resolveResponseDeadline(distribution, defaultTimezoneId);
  return {
    contractedRequest: {
      carriers: distribution.contractedCarriers.map(c => toContractedCarrierInDTO(c, distribution.contractedPriceTable)),
      priceTable: toPriceTableDTO(distribution.contractedPriceTable),
      ladenFactor: distribution.ladenFactor ? distribution.ladenFactorValue : undefined,
      allowPartialDelivery: distribution.ladenFactor ? !!distribution.allowPartialDelivery : false,
      responseDeadlineLocalTime: apiPragueFromDayjsLocalWithFormat(resolvedDeadline.localTime, resolvedDeadline.timezone),
      responseDeadlineTimezone: resolvedDeadline.timezone,
      utcEnd: apiUtcFromDayjs(resolvedDeadline.localTime),
      timezoneId: resolvedDeadline.timezone,
    },
  } as SaveDistributionRequestInDTO;
};

export const mapDistributionSelectorToRequest = (
  distribution: CarrierDistributionSelectorData | undefined,
  defaultTimezoneId: string,
  pickupTz?: string,
  pickupTimeslots?: GlobalTimeslots,
): SaveDistributionRequestInDTO => {
  // CONTRACTED has no selectedItems — its carriers live in contractedCarriers.
  if (distribution?.distributionType === 'CONTRACTED') return mapContractedDistributionToRequest(distribution, defaultTimezoneId);
  if (distribution == null || isNullOrUndefined(distribution.selectedItems) || isEmptyArray(distribution.selectedItems)) return null;
  const data = { distribution };

  const dachserItem = data.distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.DACHSER));
  const schenkerItem = data.distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.SCHENKER));
  const dhlItem = data.distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.DHL));

  const pickupDate = dhlItem?._dhlPickupCapabilities?.localCutoffDateAndTime?.substring(0, 10);
  const dhlAdditional = dhlItem?._dhlCourierRequested
    ? {
      dhlCourierRequested: true,
      dhlPickupReadyTime:
        pickupDate && dhlItem._dhlPickupReadyTime && pickupTz
          ? dayjsFromDayAndTimeStrings(pickupDate, `${dhlItem._dhlPickupReadyTime}:00`, pickupTz)?.toISOString()
          : undefined,
      dhlPickupCloseTime: dhlItem._dhlPickupCloseTime ? `${dhlItem._dhlPickupCloseTime}:00` : undefined,
    }
    : {};

  const trimmedPayer = dhlItem?._dhlPayerAccountNumber?.trim() ?? '';
  const dhlPayerFields = dhlItem?._dhlDifferentPayer
    ? {
      dhlPayerAccountNumber: trimmedPayer,
      dhlDutiesTaxesAccountNumber: dhlItem._dhlApplyToDutiesTaxes ? trimmedPayer : undefined,
    }
    : {};

  const fedexItem = data.distribution?.selectedItems?.find(item => item.apiTypes?.includes(ApiTypesEnum.FEDEX));
  // pickup date chosen from the open loading days FedEx offered; the first open day
  // of the loading location's timeslots when the availability has not answered yet
  const fedexFirstOpenDay = pickupTimeslots?.dayWithInterval
    ?.find((day: GlobalDayWithInterval) => day.openingMode !== OpeningModeEnum.CLOSED);
  const fedexPickupDate = fedexItem?._fedexPickupDate ?? timeslotLocalDay(fedexFirstOpenDay);
  const fedexAdditional = fedexItem?._fedexCourierRequested
    ? {
      fedexCourierRequested: true,
      fedexPickupReadyTime:
        fedexPickupDate && fedexItem._fedexPickupReadyTime && pickupTz
          ? dayjsFromDayAndTimeStrings(fedexPickupDate, `${fedexItem._fedexPickupReadyTime}:00`, pickupTz)?.toISOString()
          : undefined,
      fedexPickupCloseTime: fedexItem._fedexPickupCloseTime ? `${fedexItem._fedexPickupCloseTime}:00` : undefined,
    }
    : {};

  if (data.distribution.distributionType === 'ASSIGN') {
    return {
      directAssign: {
        price: data.distribution.price,
        carrierId: data.distribution.selectedItems[0].id,
        notifyAssignedCarrier: data.distribution.notifyAssignedCarrier,
        userIds: getSelectedUserIds(data.distribution.selectedItems),
      },
    } as SaveDistributionRequestInDTO;
  }

  if (data.distribution.distributionType !== 'FIXED' && data.distribution.distributionType !== 'SPOT') return undefined;

  // FIXED and SPOT carry an identical payload; only the request envelope differs.
  const resolvedDeadline = resolveResponseDeadline(data.distribution, defaultTimezoneId);
  const request = {
    price: data.distribution.price,
    responseDeadlineLocalTime: apiPragueFromDayjsLocalWithFormat(resolvedDeadline.localTime, resolvedDeadline.timezone),
    responseDeadlineTimezone: resolvedDeadline.timezone,
    utcEnd: apiUtcFromDayjs(resolvedDeadline.localTime),
    timezoneId: resolvedDeadline.timezone,
    selectedItems: data.distribution.selectedItems.map(mapSelectedItem),
    currencyLockType: data.distribution.currencyLockType,
    additionalData: {
      dachserProductType: dachserItem?._dachserProductTypeCode,
      dachserDeliveryTerm: dachserItem?._dachserDeliveryTermCode,
      schenkerProductType: schenkerItem?._schenkerProductTypeCode,
      schenkerAddonsType: schenkerItem?._schenkerAddonsTypeCodes,
      dhlProductType: dhlItem?._dhlProductTypeCode,
      ...dhlAdditional,
      ...dhlPayerFields,
      fedexProductType: fedexItem?._fedexProductTypeCode,
      ...fedexAdditional,
      fedexPayerType: fedexItem?._fedexPayerType,
      fedexPayerAccountNumber: fedexItem?._fedexPayerAccountNumber,
      fedexPayerCountry: fedexItem?._fedexPayerCountry,
      fedexDeclaredValue: fedexItem?._fedexDeclaredValue,
      fedexDeclaredValueCurrency: fedexItem?._fedexDeclaredValueCurrency,
      fedexSignatureOption: fedexItem?._fedexSignatureOption,
      fedexPackagingType: fedexItem?._fedexPackagingType,
      fedexBookingConfirmationNumber: fedexItem?._fedexBookingConfirmationNumber,
      fedexAccountId: fedexItem?._fedexAccountId,
    },
    allowPartialDelivery: data.distribution.ladenFactor ? data.distribution.allowPartialDelivery : null,
    ladenFactor: data.distribution.ladenFactor ? data.distribution.ladenFactorValue : null,
  };

  return (data.distribution.distributionType === 'FIXED' ? { fixedRequest: request } : { spotRequest: request }) as SaveDistributionRequestInDTO;
};

export const mapDistribution = (data: NewExpeditionForm, defaultTimezoneId: string): SaveDistributionRequestInDTO => {
  const pickupLocation = data.locations?.[0];
  const pickupTz =
    pickupLocation?.localityMode === LocalityEntryModeEnum.MANUALLY ? pickupLocation?.manualAddress?.timezoneId : pickupLocation?.location?.timezoneId;
  return mapDistributionSelectorToRequest(data.distribution, defaultTimezoneId, pickupTz, pickupLocation?.timeslots);
};

/**
 * Where the form content came from. `ROUTE` is the entity the page was opened for (duplicate, or create
 * from a supplier notification); `FAVORITES` is a "Prefill from favorites" pick, always an expedition.
 * At most one applies: a form is duplicated, prefilled, or neither.
 */
export interface PrefilledEntity {
  id: string;
  type: EntityTypeEnum;
  origin: 'ROUTE' | 'FAVORITES';
}

export interface SaveExpeditionContext {
  /** The entity the form was pre-filled from, if any — decides the SN link and the audited source. */
  prefilledEntity?: PrefilledEntity;
  prefillOptions?: PrefillOptions;
  /** Falls back for the distribution response deadline when the distribution carries no timezone. */
  defaultTimezoneId: string;
  /** Present only when this shipment replaces others; the backend closes them. */
  mergeInfo?: MergeInfoInDTO;
}

export const buildSaveExpeditionRequest = (data: NewExpeditionForm, ctx: SaveExpeditionContext): SaveExpeditionRequestInDTO => {
  const { prefilledEntity, prefillOptions, defaultTimezoneId, mergeInfo } = ctx;
  const fromSupplierNotification = prefilledEntity?.type === EntityTypeEnum.SUPPLIER_NOTIFICATION;
  const fromExpedition = prefilledEntity?.type === EntityTypeEnum.EXPEDITION ? prefilledEntity : null;

  return {
    applicationId: data.customApplicationId,
    fromSupplierNotificationId: fromSupplierNotification
      ? prefilledEntity.id
      : prefillOptions?.linkToSN
        ? data.supplierNotificationLink.supplierNotificationId
        : null,
    linkToSN: fromSupplierNotification || prefillOptions?.linkToSN,
    duplicatedFromExpeditionId: fromExpedition?.origin === 'ROUTE' ? fromExpedition.id : null,
    prefilledFromExpeditionId: fromExpedition?.origin === 'FAVORITES' ? fromExpedition.id : null,
    name: data.name,
    locations: data.locations.map(t => mapLocation(t)),
    distribution: mapDistribution(data, defaultTimezoneId),
    expeditionType: data.expeditionType,
    labelIds: data.labels?.map(label => label.labelId),
    cargoType: data.cargoType,
    incoterms: data.incoterms,
    carrierNote: data.carrierNote,
    loadingTypes: data.loadingTypes,
    truckTypeCode: data.truckTypeCode,
    transportationType: data.transportationType,
    requirements: data.specialRequirements,
    responsiblePersonId: data.person?.id,
    notifiedTargets: data.notifiedTargets,
    invoiceState: data.invoiceState,
    disponentPersonId: data.disponentPerson?.id,
    pairingSymbol: data.pairingSymbol,
    internalNote: data.internalNote,
    rebilling: data.rebilling
      ? {
        invoiceRebillingType: data.rebilling.invoiceRebillingType,
        invoiceDepartment: data.rebilling.invoiceDepartment,
        invoicePartnerId: data.rebilling.invoicePartner?.companyId,
      }
      : null,
    folderIds: data.folders?.map(folder => folder.id) ?? [],
    customsDeclarationCompanyIds: data.customsDeclarationCompanies?.map(company => company.id),
    attachments: data.attachments?.map(t => ({
      attachmentId: t.attachmentId,
      documentType: t.documentType,
      userNote: t.userNote,
      userDateLocalDate: t.userDateLocalDate ?? null,
      accessType: t.accessType ? t.accessType : AccessTypeEnum.ALL,
      labelIds: t.labels?.map(l => l.labelId) ?? [],
    })),
    copySNAttachments: false,
    copyAttachments: false,
    internalInfoAccountCodeListId: data.internalInfoAccountCodeListId,
    internalInfoCostCenterCodeListId: data.internalInfoCostCenterCodeListId,
    internalInfoCostUnitCodeListId: data.internalInfoCostUnitCodeListId,
    internalInfoProjectCodeListId: data.internalInfoProjectCodeListId,
    huEkaerNumber: data.huEkaerNumber,
    roUitNumber: data.roUitNumber,
    utnCode: data.utnCode,
    polandSent: data.polandSent,
    tmplV2Id: data.tmplV2Id,
    mergeInfo,
  } as SaveExpeditionRequestInDTO;
};